import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { IDeleteDataResponseAndPayload, TResponse, TResponseWithQData } from "../../../types/common";
import { IClient, IClientCreatePayload, IClientUpdatePayload } from "../../../types/api/client";
import { listClient, clientDetail, createClient, updateClient, deleteClient, listClientSites, createClientSite, updateClientSite, deleteClientSite } from "./callbacks";
import axiosInstance from "../../../lib/axios";

export const useClientList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IClient[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponseWithQData<IClient[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IClient[]>, Error>({
    queryKey: [API_ENDPOINTS.CLIENT_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listClient(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams(params as QData);
    },
  };
};

export const useClientDetail = (
  id: string | number,
  options?: Omit<UseQueryOptions<TResponse<IClient>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IClient>, Error> => {
  return useQuery<TResponse<IClient>, Error>({
    queryKey: [API_ENDPOINTS.CLIENT_DETAIL, id],
    queryFn: async () => {
      const res = await clientDetail(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const useClientCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IClient>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IClient>, unknown, IClientCreatePayload> = {}
): UseMutationResult<TResponse<IClient>, unknown, IClientCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IClient>, unknown, IClientCreatePayload>({
    mutationFn: async (data) => {
      const res = await createClient(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.CLIENT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: [res.data, ...old.data],
            extra: { ...old.extra, total: (old.extra?.total ?? 0) + 1 },
          };
        }
      );
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useClientUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IClient>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IClient>, unknown, IClientUpdatePayload> = {}
): UseMutationResult<TResponse<IClient>, unknown, IClientUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IClient>, unknown, IClientUpdatePayload>({
    mutationFn: async (data) => {
      const res = await updateClient(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.CLIENT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((c: IClient) => (c.id === res.data?.id ? res.data : c)),
          };
        }
      );
      queryClient.setQueryData([API_ENDPOINTS.CLIENT_DETAIL, res.data?.id], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

// ── Client Sites Hooks ────────────────────────────────────────────────────────

export const useClientSites = (
  clientId: number,
  options?: Omit<UseQueryOptions<TResponse<any[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<any[]>, Error> => {
  return useQuery<TResponse<any[]>, Error>({
    queryKey: ["client_sites", clientId],
    queryFn: async () => {
      const res = await listClientSites(clientId);
      return res.data;
    },
    enabled: !!clientId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    ...options,
  });
};

export const useClientSiteCreate = (
  clientId: number,
  options?: { onSuccess?: (data: any) => void; onError?: (e: any) => void }
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await createClientSite(clientId, data);
      return res.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["client_sites", clientId] });
      options?.onSuccess?.(res.data);
    },
    onError: options?.onError,
  });
};

export const useClientSiteUpdate = (
  clientId: number,
  options?: { onSuccess?: (data: any) => void; onError?: (e: any) => void }
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, data }: { siteId: number; data: any }) => {
      const res = await updateClientSite(clientId, siteId, data);
      return res.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["client_sites", clientId] });
      options?.onSuccess?.(res.data);
    },
    onError: options?.onError,
  });
};

export const useClientSiteDelete = (
  clientId: number,
  options?: { onSuccess?: () => void; onError?: (e: any) => void }
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (siteId: number) => {
      const res = await deleteClientSite(clientId, siteId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client_sites", clientId] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useClientDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> = {}
): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
    mutationFn: async (data) => {
      const res = await deleteClient(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.CLIENT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const deletedId = Number(vars.ids);
          return {
            ...old,
            data: old.data.filter((c: IClient) => c.id !== deletedId),
            extra: { ...old.extra, total: Math.max((old.extra?.total ?? 1) - 1, 0) },
          };
        }
      );
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

// ── Client Contacts Hooks ─────────────────────────────────────────────────────

export const useClientContacts = (clientId: number | null) =>
  useQuery({
    queryKey: ["client-contacts", clientId],
    queryFn: () =>
      axiosInstance
        .get(`${API_ENDPOINTS.CLIENT_SITES}/${clientId}/contacts`)
        .then((r) => r.data),
    enabled: !!clientId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useClientContactCreate = (
  options?: { onSuccess?: (data: any) => void; onError?: (e: any) => void }
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: number; data: any }) =>
      axiosInstance
        .post(`${API_ENDPOINTS.CLIENT_SITES}/${clientId}/contacts`, data)
        .then((r) => r.data),
    onSuccess: (res, { clientId }) => {
      qc.invalidateQueries({ queryKey: ["client-contacts", clientId] });
      options?.onSuccess?.(res);
    },
    onError: options?.onError,
  });
};

export const useClientContactDelete = (
  options?: { onSuccess?: () => void; onError?: (e: any) => void }
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, contactId }: { clientId: number; contactId: number }) =>
      axiosInstance
        .delete(`${API_ENDPOINTS.CLIENT_SITES}/${clientId}/contacts/${contactId}`)
        .then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      qc.invalidateQueries({ queryKey: ["client-contacts", clientId] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};
